import React, { useState, useEffect } from 'react';
import { 
  Radar, 
  Lightbulb,
  AlertTriangle, 
  Zap,
  Globe,
  Clock,
  RefreshCw,
  Calendar,
  Mail,
  ArrowRight,
  TrendingUp,
  CheckCircle,
  Send,
  X,
  Info,
  ChevronRight,
  Target,
  BrainCircuit,
  Shield,
  Users,
  PieChart,
  Heart,
  ShoppingCart,
  Cpu,
  Rocket,
  BarChart3,
  Layers,
  Star,
  Filter,
  Building2,
  Briefcase,
  Settings,
  Sparkles,
  FileText,
  Phone,
  Search
} from 'lucide-react';

// Import components from main site
import HeaderNav from './HeaderNav';
import FooterLan from './FooterLan';

// Color palette aligned with main site
const COLORS = {
  primary: '#ce2223',
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',
  primaryLighter: '#fef2f2',
  secondary: '#fdb52a',
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb',
  black: '#111827',
  darkGray: '#374151',
  mediumGray: '#6b7280',
  lightGray: '#e5e7eb',
  offWhite: '#f9fafb',
  white: '#ffffff',
  success: '#10b981',
  successLight: '#ecfdf5'
};

// Innovation categories with consistent styling
const INNOVATION_CATEGORIES = {
  product: {
    label: 'Product Innovation',
    icon: Rocket,
    bgClass: 'bg-blue-50',
    textClass: 'text-blue-700',
    borderClass: 'border-blue-200',
    iconBg: 'bg-blue-100',
    description: 'New products, features, or services'
  },
  process: {
    label: 'Process Innovation',
    icon: Settings,
    bgClass: 'bg-green-50',
    textClass: 'text-green-700',
    borderClass: 'border-green-200',
    iconBg: 'bg-green-100',
    description: 'Operational improvements and efficiency gains'
  },
  technology: {
    label: 'Technology Innovation',
    icon: Cpu,
    bgClass: 'bg-purple-50',
    textClass: 'text-purple-700',
    borderClass: 'border-purple-200',
    iconBg: 'bg-purple-100',
    description: 'Digital transformation and new technologies'
  },
  business_model: {
    label: 'Business Model Innovation',
    icon: Building2,
    bgClass: 'bg-amber-50',
    textClass: 'text-amber-700',
    borderClass: 'border-amber-200',
    iconBg: 'bg-amber-100',
    description: 'New ways of creating and capturing value'
  },
  customer: {
    label: 'Customer Experience',
    icon: Heart,
    bgClass: 'bg-pink-50',
    textClass: 'text-pink-700',
    borderClass: 'border-pink-200',
    iconBg: 'bg-pink-100',
    description: 'Enhanced customer journey and satisfaction'
  }
};

// Industries for context
const INDUSTRIES = [
  'Technology & Software',
  'Healthcare & Biotech',
  'Financial Services',
  'Manufacturing',
  'Retail & E-commerce',
  'Education',
  'Real Estate',
  'Transportation & Logistics',
  'Energy & Utilities',
  'Professional Services',
  'Media & Entertainment',
  'Food & Beverage',
  'Other'
];

// Company sizes
const COMPANY_SIZES = [
  { value: 'startup', label: 'Startup (1-50 employees)' },
  { value: 'small', label: 'Small Business (51-200 employees)' },
  { value: 'medium', label: 'Medium Business (201-1000 employees)' },
  { value: 'large', label: 'Large Enterprise (1000+ employees)' }
];

// Strategic goals
const STRATEGIC_GOALS = [
  { id: 'growth', label: 'Revenue Growth', icon: TrendingUp },
  { id: 'efficiency', label: 'Operational Efficiency', icon: Settings },
  { id: 'experience', label: 'Customer Experience', icon: Heart },
  { id: 'digital', label: 'Digital Transformation', icon: Cpu },
  { id: 'competitive', label: 'Competitive Advantage', icon: Target },
  { id: 'innovation', label: 'Innovation Culture', icon: Lightbulb },
  { id: 'sustainability', label: 'Sustainability', icon: Globe },
  { id: 'talent', label: 'Talent & Skills', icon: Users }
];

// Enhanced Button component
const Button = ({ 
  children, 
  onClick, 
  isLoading = false, 
  fullWidth = false, 
  variant = 'primary', 
  icon, 
  className = '', 
  disabled = false,
  size = 'md'
}) => {
  const baseStyle = "font-medium transition-all duration-300 flex items-center justify-center shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500";
  
  const sizes = {
    sm: "py-2 px-4 text-sm rounded-md",
    md: "py-3 px-6 text-base rounded-lg",
    lg: "py-4 px-8 text-lg rounded-xl"
  };
  
  const variants = {
    primary: `bg-red-600 hover:bg-red-700 text-white ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`,
    outline: `bg-white border-2 border-red-600 text-red-600 hover:bg-red-50 ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`,
    secondary: `bg-gray-100 hover:bg-gray-200 text-gray-700 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
  };

  return (
    <button
      onClick={onClick}
      disabled={isLoading || disabled}
      className={`${baseStyle} ${sizes[size]} ${variants[variant]} ${fullWidth ? 'w-full' : ''} ${className}`}
    >
      {isLoading ? (
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
      ) : (
        icon && <span className="mr-2">{icon}</span>
      )}
      {children}
    </button>
  );
};

// Toast notification with better mobile support
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 5000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const styles = {
    success: 'bg-green-50 text-green-800 border-green-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200'
  };
  
  return (
    <div className={`fixed top-20 left-4 right-4 sm:right-4 sm:left-auto sm:max-w-sm z-50 px-4 py-3 rounded-lg shadow-lg border flex items-center gap-3 ${styles[type]}`}>
      <CheckCircle className="h-5 w-5 flex-shrink-0" />
      <p className="font-medium flex-1">{message}</p>
      <button 
        onClick={onClose} 
        className="flex-shrink-0 p-1 hover:bg-black/10 rounded"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Opportunity card component with mobile improvements
const OpportunityCard = ({ opportunity, index }) => {
  const category = INNOVATION_CATEGORIES[opportunity.category] || INNOVATION_CATEGORIES.product;
  const CategoryIcon = category.icon;
  
  const getImpactColor = (impact) => {
    if (impact >= 8) return 'bg-green-100 text-green-800 border-green-200';
    if (impact >= 6) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };
  
  const getEffortColor = (effort) => {
    if (effort <= 3) return 'bg-green-100 text-green-800';
    if (effort <= 6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };
  
  const getEffortLabel = (effort) => {
    if (effort <= 3) return 'Low Effort';
    if (effort <= 6) return 'Medium Effort';
    return 'High Effort';
  };
  
  return (
    <article className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${category.iconBg} flex items-center justify-center flex-shrink-0`}>
              <CategoryIcon className={`h-5 w-5 ${category.textClass}`} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-gray-900 text-lg leading-tight">{opportunity.title}</h3>
              <span className={`text-sm ${category.textClass} font-medium`}>{category.label}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 self-start">
            <Star className="h-4 w-4 text-yellow-500" />
            <span className="text-sm font-medium text-gray-700">{opportunity.priority_score}/10</span>
          </div>
        </div>
        
        {/* Description */}
        <p className="text-gray-700 mb-4 leading-relaxed">{opportunity.description}</p>
        
        {/* Metrics */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-4">
          <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getImpactColor(opportunity.impact)}`}>
            Impact: {opportunity.impact}/10
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${getEffortColor(opportunity.effort)}`}>
            {getEffortLabel(opportunity.effort)}
          </div>
          <div className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {opportunity.timeline}
          </div>
        </div>
        
        {/* Implementation Steps */}
        {opportunity.implementation && (
          <div className={`${category.bgClass} border ${category.borderClass} rounded-lg p-4`}>
            <div className="flex items-start gap-3">
              <ArrowRight className={`h-4 w-4 ${category.textClass} mt-1 flex-shrink-0`} />
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-1">Next Steps:</h4>
                <p className="text-sm text-gray-700 leading-relaxed">{opportunity.implementation}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

// Loading animation with better mobile support
const LoadingAnimation = () => {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => Math.min(prev + 3, 95));
    }, 150);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 sm:p-8 max-w-md mx-auto text-center">
      <div className="w-16 h-16 mx-auto mb-6">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          <circle
            className="text-gray-200"
            strokeWidth="8"
            stroke="currentColor"
            fill="transparent"
            r="40"
            cx="50"
            cy="50"
          />
          <circle
            className="text-red-600 transition-all duration-300"
            strokeWidth="8"
            strokeDasharray={251}
            strokeDashoffset={251 - (progress / 100) * 251}
            strokeLinecap="round"
            stroke="currentColor"
            fill="transparent"
            r="40"
            cx="50"
            cy="50"
          />
        </svg>
      </div>
      
      <h3 className="text-xl font-bold text-gray-900 mb-2">Mapping Innovation Opportunities</h3>
      <div className="flex items-center justify-center gap-2 text-red-600 mb-4">
        <Search className="h-5 w-5" />
        <span className="font-medium">Analyzing your business context...</span>
      </div>
      <p className="text-gray-600 text-sm leading-relaxed">
        This takes about 30 seconds while we identify high-impact opportunities tailored to your industry and strategic goals.
      </p>
    </div>
  );
};

// Email capture component with mobile improvements
const EmailCapture = ({ onSubmit, isSubmitting }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  
  const handleSubmit = () => {
    if (!email || !email.includes('@')) return;
    onSubmit({ email, name });
  };
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-3">
          <FileText className="h-6 w-6 text-red-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Get Complete Innovation Strategy</h3>
        <p className="text-gray-600 text-sm">Download detailed implementation roadmap with competitive analysis and 90-day action plan</p>
      </div>
      
      <div className="mb-6 space-y-3">
        <h4 className="font-medium text-gray-900 text-sm">Your strategy includes:</h4>
        {[
          "Detailed implementation roadmap with timelines and milestones",
          "Competitive benchmarking and market analysis for each opportunity", 
          "90-day action plan with specific next steps and resource requirements",
          "ROI projections and success metrics for measuring innovation impact"
        ].map((item, idx) => (
          <div key={idx} className="flex items-start gap-3 text-sm text-gray-600">
            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span className="leading-relaxed">{item}</span>
          </div>
        ))}
      </div>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="name-input" className="block text-sm font-medium text-gray-700 mb-2">
            Name
          </label>
          <input
            id="name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          />
        </div>
        
        <div>
          <label htmlFor="email-input" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            id="email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          />
        </div>
        
        <Button
          onClick={handleSubmit}
          disabled={!email || !email.includes('@')}
          isLoading={isSubmitting}
          icon={<Send className="h-4 w-4" />}
          fullWidth
        >
          Send Innovation Strategy
        </Button>
      </div>
      
      <p className="text-xs text-gray-500 text-center mt-4">
        We respect your privacy and never share your information
      </p>
    </div>
  );
};

// Success message
const SubmissionSuccess = ({ email }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 sm:p-8 text-center max-w-md mx-auto">
      <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
        <CheckCircle className="h-8 w-8 text-green-600" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">Innovation Strategy Delivered!</h3>
      <p className="text-gray-600 mb-4">
        Your comprehensive innovation roadmap has been sent to <span className="font-semibold text-gray-900">{email}</span>
      </p>
      <p className="text-gray-600 text-sm">
        Our innovation consultants will follow up within 24 hours to discuss implementation priorities and next steps.
      </p>
    </div>
  );
};

// Business context form with mobile improvements
const BusinessContextForm = ({ onSubmit }) => {
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [challenges, setChallenges] = useState('');
  const [currentInnovations, setCurrentInnovations] = useState('');
  
  const handleGoalToggle = (goalId) => {
    setSelectedGoals(prev => 
      prev.includes(goalId) 
        ? prev.filter(id => id !== goalId)
        : [...prev, goalId]
    );
  };
  
  const handleSubmit = () => {
    if (!industry || !companySize || selectedGoals.length === 0) return;
    
    onSubmit({
      industry,
      companySize,
      goals: selectedGoals,
      challenges,
      currentInnovations
    });
  };
  
  const isValid = industry && companySize && selectedGoals.length > 0;
  
  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
        Tell us about your business
      </h2>
      <p className="text-gray-600 text-center mb-8">
        We'll analyze your context and identify the most relevant innovation opportunities for your specific situation.
      </p>
      
      <div className="space-y-6">
        {/* Industry */}
        <div>
          <label htmlFor="industry-select" className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
          <select
            id="industry-select"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          >
            <option value="">Select your industry</option>
            {INDUSTRIES.map(ind => (
              <option key={ind} value={ind}>{ind}</option>
            ))}
          </select>
        </div>
        
        {/* Company Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Company Size</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {COMPANY_SIZES.map(size => (
              <button
                key={size.value}
                onClick={() => setCompanySize(size.value)}
                className={`p-3 rounded-lg border text-left transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                  companySize === size.value 
                    ? 'border-red-500 bg-red-50 text-red-700' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="font-medium">{size.label}</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Strategic Goals */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Strategic Goals (select 2-4 priorities)
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {STRATEGIC_GOALS.map(goal => {
              const GoalIcon = goal.icon;
              return (
                <button
                  key={goal.id}
                  onClick={() => handleGoalToggle(goal.id)}
                  className={`p-3 rounded-lg border text-left transition-all flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                    selectedGoals.includes(goal.id)
                      ? 'border-red-500 bg-red-50 text-red-700' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <GoalIcon className="h-5 w-5 flex-shrink-0" />
                  <span className="font-medium">{goal.label}</span>
                </button>
              );
            })}
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Selected: {selectedGoals.length} goals
          </p>
        </div>
        
        {/* Current Challenges */}
        <div>
          <label htmlFor="challenges-input" className="block text-sm font-medium text-gray-700 mb-2">
            Current Business Challenges (optional)
          </label>
          <textarea
            id="challenges-input"
            value={challenges}
            onChange={(e) => setChallenges(e.target.value)}
            placeholder="What specific challenges is your business facing that innovation could help solve?"
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none resize-y"
            rows={3}
          />
        </div>
        
        {/* Current Innovation Efforts */}
        <div>
          <label htmlFor="innovations-input" className="block text-sm font-medium text-gray-700 mb-2">
            Current Innovation Efforts (optional)
          </label>
          <textarea
            id="innovations-input"
            value={currentInnovations}
            onChange={(e) => setCurrentInnovations(e.target.value)}
            placeholder="What innovation initiatives are you already working on?"
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none resize-y"
            rows={3}
          />
        </div>
        
        <div className="text-center pt-4">
          <Button
            onClick={handleSubmit}
            disabled={!isValid}
            icon={<Search className="h-5 w-5" />}
            size="lg"
          >
            Map Innovation Opportunities
          </Button>
        </div>
      </div>
    </section>
  );
};

// Priority matrix visualization with mobile improvements
const PriorityMatrix = ({ opportunities }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-red-600" />
        Impact vs Effort Matrix
      </h3>
      
      <div className="relative bg-gray-50 rounded-lg p-4" style={{ height: '280px' }}>
        {/* Axis labels */}
        <div className="absolute -left-6 top-1/2 transform -rotate-90 text-xs font-medium text-gray-600">
          Impact
        </div>
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-5 text-xs font-medium text-gray-600">
          Effort
        </div>
        
        {/* Quadrant labels */}
        <div className="absolute top-2 left-2 text-xs text-gray-500 font-medium">High Impact<br/>Low Effort</div>
        <div className="absolute top-2 right-2 text-xs text-gray-500 font-medium">High Impact<br/>High Effort</div>
        <div className="absolute bottom-2 left-2 text-xs text-gray-500 font-medium">Low Impact<br/>Low Effort</div>
        <div className="absolute bottom-2 right-2 text-xs text-gray-500 font-medium">Low Impact<br/>High Effort</div>
        
        {/* Grid lines */}
        <div className="absolute inset-4 border-l border-b border-gray-300">
          <div className="absolute top-1/2 left-0 right-0 border-t border-gray-200"></div>
          <div className="absolute left-1/2 top-0 bottom-0 border-l border-gray-200"></div>
        </div>
        
        {/* Opportunity dots */}
        {opportunities.map((opp, idx) => {
          const x = (opp.effort / 10) * 80 + 10; // 10-90% of width
          const y = 90 - (opp.impact / 10) * 80; // Inverted Y axis
          const category = INNOVATION_CATEGORIES[opp.category] || INNOVATION_CATEGORIES.product;
          
          return (
            <div
              key={idx}
              className={`absolute w-3 h-3 rounded-full ${category.iconBg} border-2 ${category.borderClass} cursor-pointer hover:scale-125 transition-transform`}
              style={{ left: `${x}%`, top: `${y}%` }}
              title={opp.title}
            />
          );
        })}
      </div>
      
      <div className="mt-4 space-y-2">
        <p className="text-xs text-gray-600 font-medium">Innovation Types:</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(INNOVATION_CATEGORIES).map(([key, category]) => (
            <div key={key} className="flex items-center gap-2 text-xs">
              <div className={`w-3 h-3 rounded-full ${category.iconBg} border ${category.borderClass}`} />
              <span className="text-gray-600">{category.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main component
const InnovationOpportunityMapper = () => {
  const [businessContext, setBusinessContext] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [toast, setToast] = useState(null);
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  const generateOpportunities = async (context) => {
    setBusinessContext(context);
    setIsLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    try {
      const response = await fetch('https://proxyapi-te6hpgqhgq-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-3-5-sonnet-20241022',
          max_tokens: 3500,
          temperature: 0.7,
          messages: [{
            role: 'user',
            content: `Based on this business context, identify 8 specific innovation opportunities:

Industry: ${context.industry}
Company Size: ${context.companySize}
Strategic Goals: ${context.goals.join(', ')}
Current Challenges: ${context.challenges || 'Not specified'}
Current Innovation Efforts: ${context.currentInnovations || 'Not specified'}

For each opportunity, provide:
- Title: Specific, actionable opportunity name
- Category: product, process, technology, business_model, or customer
- Description: Clear 2-sentence explanation of the opportunity and its business value
- Impact: Score 1-10 (potential business impact)
- Effort: Score 1-10 (implementation difficulty and resource requirements)
- Priority_score: Overall priority rating 1-10
- Timeline: Implementation timeframe (e.g., "3-6 months", "6-12 months", "12+ months")
- Implementation: Specific first 2-3 steps to pursue this opportunity

Focus on opportunities that align with their strategic goals and are relevant to their industry and company size. Keep language simple and business-focused.

Respond in this JSON format:
{
  "opportunities": [
    {
      "title": "Opportunity Name",
      "category": "product|process|technology|business_model|customer",
      "description": "Clear explanation of the opportunity and why it matters for this business.",
      "impact": 8,
      "effort": 4,
      "priority_score": 8,
      "timeline": "3-6 months",
      "implementation": "Specific actionable steps to get started with this opportunity."
    }
  ]
}`
          }]
        })
      });

      if (!response.ok) throw new Error('Failed to generate opportunities');
      
      const data = await response.json();
      const jsonMatch = data.content[0].text.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const opportunitiesData = JSON.parse(jsonMatch[0]);
        setTimeout(() => {
          setOpportunities(opportunitiesData.opportunities || []);
          setIsLoading(false);
        }, 2000);
      } else {
        throw new Error('Could not parse response');
      }
    } catch (error) {
      console.error('Error:', error);
      setIsLoading(false);
      setToast({ message: 'Unable to generate opportunities. Please try again.', type: 'error' });
    }
  };
  
  const handleEmailSubmit = (data) => {
    setIsEmailSubmitting(true);
    setTimeout(() => {
      setIsEmailSubmitting(false);
      setEmailSubmitted(true);
      setSubmittedEmail(data.email);
      setToast({ message: 'Innovation strategy sent to your email successfully!', type: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1500);
  };
  
  const handleReset = () => {
    setBusinessContext(null);
    setOpportunities([]);
    setEmailSubmitted(false);
    setSubmittedEmail('');
    setFilterCategory('all');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const filteredOpportunities = filterCategory === 'all' 
    ? opportunities 
    : opportunities.filter(opp => opp.category === filterCategory);
  
  const sortedOpportunities = [...filteredOpportunities].sort((a, b) => b.priority_score - a.priority_score);

  // SEO metadata (would be handled by helmet in real implementation)
  useEffect(() => {
    document.title = "Innovation Opportunity Mapper - Strategic Innovation Planning Tool | Lusidea";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover and prioritize high-impact innovation opportunities for your business. Get strategic insights, implementation roadmaps, and competitive analysis tailored to your industry.');
    }
  }, []);
  
  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderNav />
      
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Header Section */}
      <header className="bg-white pt-24 sm:pt-28 pb-12 sm:pb-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-2xl flex items-center justify-center mb-6">
            <Lightbulb className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Innovation Opportunity Mapper
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Discover and prioritize the highest-impact innovation opportunities tailored to your business context and strategic goals
          </p>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        
        {/* Input Section */}
        {!businessContext && !isLoading && opportunities.length === 0 && !emailSubmitted && (
          <div className="space-y-8">
            <BusinessContextForm onSubmit={generateOpportunities} />
            
            {/* How it works */}
            <section className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <Info className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-3">How this tool works</h3>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    Our analysis system examines your business context, industry trends, and strategic goals to identify 8 customized innovation opportunities. 
                    Each opportunity is scored for impact and implementation effort, then prioritized for maximum business value and strategic alignment.
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}
        
        {/* Loading State */}
        {isLoading && <LoadingAnimation />}
        
        {/* Results */}
        {!isLoading && opportunities.length > 0 && !emailSubmitted && (
          <div className="space-y-8">
            {/* Results Header */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Your Innovation Opportunities</h2>
                    <p className="text-gray-600 text-sm">{businessContext?.industry} • {opportunities.length} opportunities identified</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Innovation Categories Overview */}
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4 text-center">Innovation Types Identified</h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {Object.entries(INNOVATION_CATEGORIES).map(([key, category]) => {
                  const CategoryIcon = category.icon;
                  const count = opportunities.filter(opp => opp.category === key).length;
                  return (
                    <div key={key} className="flex flex-col items-center text-center p-3 rounded-lg bg-gray-50">
                      <div className={`w-10 h-10 rounded-lg ${category.iconBg} flex items-center justify-center mb-2`}>
                        <CategoryIcon className={`h-5 w-5 ${category.textClass}`} />
                      </div>
                      <span className="text-xs text-gray-700 font-medium">{category.label}</span>
                      {count > 0 && <span className="text-xs text-gray-500 mt-1">({count})</span>}
                    </div>
                  );
                })}
              </div>
            </section>
            
            {/* Filter Buttons */}
            <section className="flex flex-wrap gap-3">
              <button
                onClick={() => setFilterCategory('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterCategory === 'all'
                    ? 'bg-red-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                All ({opportunities.length})
              </button>
              {Object.entries(INNOVATION_CATEGORIES).map(([key, category]) => {
                const count = opportunities.filter(opp => opp.category === key).length;
                if (count === 0) return null;
                
                return (
                  <button
                    key={key}
                    onClick={() => setFilterCategory(key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filterCategory === key
                        ? 'bg-red-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {category.label} ({count})
                  </button>
                );
              })}
            </section>
            
            {/* Opportunity Cards */}
            <section className="space-y-6">
              {sortedOpportunities.map((opportunity, idx) => (
                <OpportunityCard key={idx} opportunity={opportunity} index={idx} />
              ))}
            </section>

            {/* Email Capture */}
            <section className="max-w-2xl mx-auto">
              <EmailCapture onSubmit={handleEmailSubmit} isSubmitting={isEmailSubmitting} />
            </section>

            {/* Priority Matrix */}
            <section className="max-w-2xl mx-auto">
              <PriorityMatrix opportunities={opportunities} />
            </section>

            {/* Innovation Types Detail */}
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Layers className="h-5 w-5 text-red-600" />
                Innovation Types Explained
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(INNOVATION_CATEGORIES).map(([key, category]) => {
                  const CategoryIcon = category.icon;
                  return (
                    <div key={key} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                      <div className={`w-8 h-8 rounded-lg ${category.iconBg} flex items-center justify-center flex-shrink-0`}>
                        <CategoryIcon className={`h-4 w-4 ${category.textClass}`} />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{category.label}</div>
                        <div className="text-xs text-gray-600 leading-relaxed">{category.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Strategy Session CTA */}
            <section className="bg-gradient-to-r from-red-50 to-red-100 rounded-xl border border-red-200 p-6 sm:p-8 text-center">
              <div className="max-w-3xl mx-auto">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Need Expert Guidance?</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Discuss implementation priorities and create a 90-day innovation roadmap with our experts. 
                  Get personalized advice on which opportunities to tackle first and how to build momentum.
                </p>
                <a
                  href="https://calendly.com/lusidea/innovation-strategy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 py-3 px-6 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 shadow-sm"
                >
                  <Calendar className="h-5 w-5" />
                  Book Strategy Session
                </a>
              </div>
            </section>

            {/* Reset Action */}
            <div className="text-center pt-4">
              <Button
                onClick={handleReset}
                variant="outline"
                icon={<RefreshCw className="h-4 w-4" />}
              >
                Start Over
              </Button>
            </div>
          </div>
        )}
        
        {/* Success State */}
        {emailSubmitted && <SubmissionSuccess email={submittedEmail} />}
      </main>
      
      <FooterLan />
      
      <style jsx global>{`
        /* Enhanced focus states for accessibility */
        input:focus, textarea:focus, select:focus {
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }
        
        /* Smooth transitions */
        * {
          transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 150ms;
        }
      `}</style>
    </div>
  );
};

export default InnovationOpportunityMapper;